import { useEffect, useMemo, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { getTenantSmartlinkUsage } from '@atlassian/search-client';

import { type ProductKeys, type ProductKeys3P } from '../../common/constants/products';
import {
	FULL_CONNECTOR_SOURCE,
	SMARTLINKS_CONNECTOR_SOURCE,
	type ThirdPartyConfigsBootstrap,
} from '../../common/constants/schemas/3p-config';
import { type SearchStoreExperience } from '../../common/constants/search';
import {
	onSmartLinkUsageFetchFail,
	onSmartLinkUsageFetchSuccess,
} from '../../common/utils/events/smart-link-usage-events';
import { serveImproveBannerTargetingExperiment } from '../../common/utils/experiments';
import { getOrderedAvailableProducts } from '../../common/utils/get-ordered-available-products';
import { useAnalytics } from '../analytics';
import { useSmartLinkUsage } from '../store/bootstrap';

import { hasEnoughUsageToShowBanner } from './thresholds';

/**
 * Hook that fetches Smart Link usage data.
 */
export const useFetchSmartLinkUsage = (
	experience: SearchStoreExperience,
	cloudId?: string,
): Record<string, number> | undefined => {
	const [smartLinkUsage, setSmartLinkUsage] = useState<Record<string, number> | undefined>(
		undefined,
	);
	const { fireAnalyticsEvent } = useAnalytics({ experience });
	// NO EXPOSURE! Allocate to short-circuit the hook if the experiment is disabled.
	const bannerTargetingCohort = serveImproveBannerTargetingExperiment({
		fireExperimentExposure: false,
	});

	useEffect(() => {
		if (bannerTargetingCohort === 'usage-threshold' && cloudId) {
			getTenantSmartlinkUsage({ siteId: cloudId })
				.then((usageData) => {
					setSmartLinkUsage(usageData);
					fireAnalyticsEvent(
						onSmartLinkUsageFetchSuccess({
							cloudId,
							experience,
							productCount: Object.keys(usageData).length,
							usages: usageData,
						}),
					);
				})
				.catch((error) => {
					fireAnalyticsEvent(
						onSmartLinkUsageFetchFail({
							cloudId,
							experience,
							reason: error instanceof Error ? error.message : String(error),
						}),
					);
				});
		}
	}, [bannerTargetingCohort, cloudId, experience, fireAnalyticsEvent]);

	return smartLinkUsage;
};

/**
 * Hook that returns a filtered list of products that have enough Smart Link usage
 * at the current site to justify f.e. showing OAuth banners.
 */
export const useProductsWithEnoughUsageToShowBanner = ({
	products,
	thirdPartyConfigs,
}: {
	products: ProductKeys[];
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
}): ProductKeys[] => {
	// NO EXPOSURE! Allocate to short-circuit the hook if the experiment is disabled.
	const cohort = serveImproveBannerTargetingExperiment({ fireExperimentExposure: false });
	const isUsageThresholdCohort = cohort === 'usage-threshold';
	const isBannerOptedOut = fg('rovo_search_3p_banner_opt_out');
	const isActive = isUsageThresholdCohort && !isBannerOptedOut;

	const usageData = useSmartLinkUsage();

	return useMemo(() => {
		if (!isActive || !usageData) {
			return products;
		}

		return products.filter((product) => {
			const config = thirdPartyConfigs[product as ProductKeys3P];

			const isSmartlinksOnly =
				config?.connectorSources?.includes(SMARTLINKS_CONNECTOR_SOURCE) &&
				!config?.connectorSources?.includes(FULL_CONNECTOR_SOURCE);

			// The logic to hide banners based on usage is only applicable to smart-link connectors
			// and only if there's no full-connector for the same product.
			if (!isSmartlinksOnly) {
				return true;
			}

			return hasEnoughUsageToShowBanner(product, usageData[product]);
		});
	}, [isActive, usageData, products, thirdPartyConfigs]);
};

/**
 * Hook for "list to pass to the placement" (FPS or QF). Orders products, applies
 * usage-threshold filter when in that cohort, and returns the list.
 * Uses fireExperimentExposure: false so exposure stays at the call site that renders the banner.
 */
export const useAvailableProductsOrderForPlacement = ({
	isOnSearchDialog,
	thirdPartyConfigs,
	availableProducts,
}: {
	isOnSearchDialog: boolean;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	availableProducts: ProductKeys[];
}): ProductKeys[] => {
	const cohort = serveImproveBannerTargetingExperiment({ fireExperimentExposure: false });

	const orderedAvailableProducts = useMemo(
		() =>
			getOrderedAvailableProducts({
				isOnSearchDialog,
				thirdPartyConfigs,
				availableProducts,
			}),
		[isOnSearchDialog, thirdPartyConfigs, availableProducts],
	);

	const productsWithSufficientUsage = useProductsWithEnoughUsageToShowBanner({
		products: orderedAvailableProducts,
		thirdPartyConfigs,
	});

	return useMemo(() => {
		if (cohort === 'usage-threshold' && productsWithSufficientUsage?.length) {
			return orderedAvailableProducts.filter((p) =>
				productsWithSufficientUsage.includes(p),
			);
		}
		return orderedAvailableProducts;
	}, [orderedAvailableProducts, cohort, productsWithSufficientUsage]);
};
